import type {
  CheckoutBookingForm,
  CheckoutField,
  CheckoutTravelerDraft,
} from '../model/checkout_source.js';
import type { CheckoutTravelerChips } from '../traveler/checkout_traveler_card.js';

import { formatTravelerDate } from '../fields/date_copy.js';

function travelerDocumentValue(
  traveler: CheckoutBookingForm['traveler'],
  travelerDraft: CheckoutTravelerDraft,
) {
  return (
    travelerDraft.document?.value ||
    traveler?.document?.value ||
    traveler?.maskedInfo?.cpf ||
    traveler?.maskedInfo?.passport ||
    traveler?.maskedInfo?.dni
  );
}

export function maskDocumentValue(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (/[*•]/.test(value)) return value;
  if (value.length <= 4) return '*'.repeat(value.length);
  return `${value.slice(0, 2)}****${value.slice(-2)}`;
}

/** A identidade que o cartão mostra: o rascunho por cima do perfil. */
function effectiveIdentity(
  traveler: CheckoutBookingForm['traveler'],
  travelerDraft: CheckoutTravelerDraft,
  locale: string,
) {
  const gender = travelerDraft.gender ?? traveler?.gender;
  return {
    nationality: (travelerDraft.nationality ?? traveler?.nationality)?.code,
    gender: gender && (gender === 'F' ? ('female' as const) : ('male' as const)),
    birthdate: formatTravelerDate(travelerDraft.birthdate ?? traveler?.birthdate, locale),
    document: maskDocumentValue(travelerDocumentValue(traveler, travelerDraft)),
  };
}

/** Os chips do cartão recolhido: só o que o manifesto do produto coleta. */
export function travelerChips(
  fields: CheckoutField[],
  traveler: CheckoutBookingForm['traveler'],
  travelerDraft: CheckoutTravelerDraft,
  locale: string,
): CheckoutTravelerChips {
  const has = (kind: CheckoutField['kind']) => fields.some((field) => field.kind === kind);
  const { nationality, gender, birthdate, document } = effectiveIdentity(
    traveler,
    travelerDraft,
    locale,
  );
  return {
    ...(has('nationality') && nationality ? { nationality } : {}),
    ...(has('gender') && gender ? { gender } : {}),
    ...(has('birthdate') && birthdate ? { birthdate } : {}),
    ...(has('document') && document ? { document } : {}),
    // O telefone é exigido pelo manifesto (voo/mobility) e nunca entra em
    // `missingRequired`: sem ele o CTA trava (#4206).
    phonePending: has('phone') && !travelerDraft.phone?.trim(),
  };
}
